import {useEffect, useRef} from 'react';
import type {RefObject} from 'react';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface DialogA11yOptions {
    /** Пока false — обработчики не навешиваются (модалка закрыта). */
    active?: boolean;
    /** Держать фокус внутри: только для настоящих модальных окон. */
    trapFocus?: boolean;
    /** Вернуть фокус на вызвавший элемент при закрытии. */
    restoreFocus?: boolean;
}

/**
 * Клавиатурный контракт модального окна (styleguide §Modals, QA-16):
 * Escape закрывает, `Tab`/`Shift+Tab` не выходят за пределы окна, закрытие
 * возвращает фокус на элемент, который окно открыл.
 *
 * Паттерн повторяет `OtpDialog`; вынесен в хук, чтобы новые диалоги получали
 * его целиком, а не по частям.
 */
export function useDialogA11y(
    dialogRef: RefObject<HTMLElement>,
    onClose: () => void,
    {active = true, trapFocus = true, restoreFocus = true}: DialogA11yOptions = {}
): void {
    // Колбэк держим в ref: иначе новая функция на каждый рендер пересоздавала бы
    // эффект, а его размонтирование возвращает фокус — форма теряла бы каретку.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!active) {
            return;
        }
        const previouslyFocused = document.activeElement as HTMLElement | null;
        // Фокус на само окно: скринридер читает заголовок, а не первую кнопку.
        dialogRef.current?.focus?.();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCloseRef.current();
                return;
            }
            if (!trapFocus || e.key !== 'Tab' || !dialogRef.current) {
                return;
            }
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
                .filter(el => el.offsetParent !== null || el === document.activeElement);
            if (focusable.length === 0) {
                e.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeEl = document.activeElement as HTMLElement | null;
            const outside = !dialogRef.current.contains(activeEl);
            if (e.shiftKey && (activeEl === first || outside)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (activeEl === last || outside)) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            if (restoreFocus) {
                previouslyFocused?.focus?.();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, trapFocus, restoreFocus]);
}

export default useDialogA11y;
